import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  IconButton,
  Typography,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import { ExistingCredential, TokenAction } from '../types';

interface TokensTableProps {
  credentials: ExistingCredential[];
  loading: boolean;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onAction?: (action: TokenAction, credential: ExistingCredential) => void;
}

/**
 * Table component for displaying existing API credentials.
 * Shows key (masked), description, creation date, last used date, and status.
 * Provides actions menu for edit and revoke.
 */
const TokensTable: React.FC<TokensTableProps> = ({
  credentials,
  loading,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onAction,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCredential, setSelectedCredential] = useState<ExistingCredential | null>(null);

  const credentialsPerPage = rowsPerPage > 0
    ? credentials.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : credentials;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, credential: ExistingCredential) => {
    setAnchorEl(event.currentTarget);
    setSelectedCredential(credential);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCredential(null);
  };

  const handleAction = (action: TokenAction) => {
    if (selectedCredential && onAction) {
      onAction(action, selectedCredential);
    }
    handleMenuClose();
  };

  const getStatusColor = (active: boolean): 'success' | 'error' => {
    return active ? 'success' : 'error';
  };

  const getStatusLabel = (active: boolean): string => {
    return active ? 'Active' : 'Revoked';
  };

  const isRevoked = selectedCredential ? !selectedCredential.active : false;

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Key</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Last Used</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right" aria-label="Token actions">{/* Actions column */}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : credentialsPerPage.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No API credentials created yet
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              credentialsPerPage.map((cred) => (
                <TableRow key={cred.id}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                    {cred.key}
                  </TableCell>
                  <TableCell>{cred.description || '—'}</TableCell>
                  <TableCell>{cred.createdDate}</TableCell>
                  <TableCell>{cred.lastUsedDate || 'Never'}</TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(cred.active)}
                      size="small"
                      color={getStatusColor(cred.active)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {cred.active && (
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, cred)}
                        aria-label="Token actions"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        className={'w-100'}
        component={'div'}
        count={credentials.length || 0}
        page={page}
        rowsPerPageOptions={[5, 10, 25]}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => handleAction('edit')}
          disabled={isRevoked}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => handleAction('revoke')}
          disabled={isRevoked}
          sx={{ color: isRevoked ? 'inherit' : 'error.main' }}
        >
          <ListItemIcon>
            <BlockIcon fontSize="small" color={isRevoked ? 'disabled' : 'error'} />
          </ListItemIcon>
          <ListItemText>{isRevoked ? 'Revoked' : 'Revoke'}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default TokensTable;
